/**
 * @file taxTypes.js
 * @description Helper for Xero tax type codes - labels and rates
 */

// Xero tax type configurations
export const TAX_TYPES = {
  // Output (Sales) tax types
  'OUTPUT2': { label: '20% VAT', rate: 0.20 },
  'OUTPUT': { label: '20% VAT', rate: 0.20 },
  'ZERORATEDOUTPUT': { label: '0% VAT', rate: 0 },
  'EXEMPTOUTPUT': { label: 'Exempt', rate: 0 },
  'ECZROUTPUT': { label: 'EC Zero', rate: 0 },
  'ECZROUTPUTSERVICES': { label: 'EC Zero Services', rate: 0 },
  'REVERSECHARGES': { label: 'Reverse Charge', rate: 0 },
  'RROUTPUT': { label: '5% VAT', rate: 0.05 },

  // Input (Purchase) tax types
  'INPUT2': { label: '20% VAT', rate: 0.20 },
  'INPUT': { label: '20% VAT', rate: 0.20 },
  'ZERORATEDINPUT': { label: '0% VAT', rate: 0 },
  'EXEMPTINPUT': { label: 'Exempt', rate: 0 },
  'RRINPUT': { label: '5% VAT', rate: 0.05 },
  'CAPEXINPUT2': { label: 'Capital 20%', rate: 0.20 },

  // Other
  'NONE': { label: 'No VAT', rate: 0 },
  'BASEXCLUDED': { label: 'Excluded', rate: 0 },
};

// Common tax types for dropdowns (output/sales focused)
export const SALES_TAX_OPTIONS = [
  { value: 'NONE', label: 'No VAT' },
  { value: 'OUTPUT2', label: '20% VAT' },
  { value: 'RROUTPUT', label: '5% VAT' },
  { value: 'ZERORATEDOUTPUT', label: '0% VAT' },
  { value: 'EXEMPTOUTPUT', label: 'VAT Exempt' },
];

// Common tax types for purchase order dropdowns
export const PURCHASE_TAX_OPTIONS = [
  { value: 'NONE', label: 'No VAT' },
  { value: 'INPUT2', label: '20% VAT' },
  { value: 'RRINPUT', label: '5% VAT' },
  { value: 'ZERORATEDINPUT', label: '0% VAT' },
  { value: 'EXEMPTINPUT', label: 'VAT Exempt' },
];

/**
 * Get the display label for a tax type
 * @param {string} taxType - The Xero tax type code
 * @returns {string} User-friendly label
 */
export function getTaxTypeLabel(taxType) {
  if (!taxType) return '-';
  return TAX_TYPES[taxType]?.label || taxType;
}

/**
 * Get the tax rate for a tax type
 * @param {string} taxType - The Xero tax type code
 * @returns {number} Tax rate as decimal (e.g., 0.20 for 20%)
 */
export function getTaxRate(taxType) {
  if (!taxType) return 0;
  return TAX_TYPES[taxType]?.rate || 0;
}

/**
 * Format tax amount for display
 * @param {number} amount - The tax amount
 * @param {string} taxType - The Xero tax type code (optional, for context)
 * @returns {string} Formatted tax display
 */
export function formatTaxDisplay(amount, taxType) {
  if (!amount && amount !== 0) return '-';

  const formatted = new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  }).format(amount);

  return formatted;
}

/**
 * Get tax percentage as display string (e.g., "20%", "5%", "0%")
 * @param {string} taxType - The Xero tax type code
 * @returns {string} Tax percentage with % symbol
 */
export function getTaxPercentDisplay(taxType) {
  if (!taxType) return '0%';
  const rate = TAX_TYPES[taxType]?.rate;
  if (rate === undefined || rate === null) return '0%';
  return `${Math.round(rate * 100)}%`;
}

/**
 * A line's VAT as one figure: the amount, with the rate in brackets after it.
 *
 * Every line table in the system showed one half or the other — Search POs and
 * Search Bills showed only the rate, the invoice line table only the rate, and
 * a reader comparing two documents had to work out the money themselves. The
 * amount is what reconciles and the rate is what explains it, so both appear
 * and in that order.
 *
 * The rate comes from the tax TYPE rather than from amount/net: a zero-rated
 * line and a 20% line that happens to have no VAT on it read identically once
 * you divide, and they are not the same thing.
 *
 * @param {number|string} amount   Tax amount on the line.
 * @param {string} taxType         Xero tax type code.
 * @param {function} formatMoney   Currency formatter from the calling screen.
 */
export function formatVatWithRate(amount, taxType, formatMoney) {
  const rate = getTaxPercentDisplay(taxType);
  const n = amount === null || amount === undefined || amount === '' ? null : Number(amount);
  if (n === null || Number.isNaN(n)) return rate;
  const money = typeof formatMoney === 'function' ? formatMoney(n) : n.toFixed(2);
  return `${money} (${rate})`;
}
